import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { apiCall } from "@/lib/api";
import { useSimulations } from "@/hooks/useSimulations";
import {
  useExecutiveDashboard,
  useManagerDashboard,
  type DashboardPeriod,
  type DashboardView,
  type ExecutiveData,
  type ManagerData,
} from "@/hooks/useDashboardData";
import { DashboardExecutiveView } from "./DashboardExecutiveView";
import { DashboardManagerView } from "./DashboardManagerView";
import { AdminNav } from "@/components/neusim/AdminNav";
import {
  downloadCsv,
  fetchRawRows,
  rawRowsToCsv,
  defaultColumnPick,
  EXEC_SECTIONS,
  MANAGER_SECTIONS,
  RAW_COLUMNS,
  type ExecSectionKey,
  type ManagerSectionKey,
  type ColumnPick,
} from "@/lib/dashboardExport";
import {
  generateExecutiveDashboardPdf,
  generateManagerDashboardPdf,
  downloadBlob,
  slugForFile,
} from "@/lib/simPdf";
import neucodeLogoWhite from "../assets/neucode-logo-white.png";
import "./DashboardPage.css";

interface ClientRow {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  projectCount: number;
  simCount: number;
}

interface ProjectRow {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  isActive: boolean;
}

interface CohortRow {
  id: string;
  name: string;
  isActive: boolean;
  client: { id: string; name: string };
  project: { id: string; name: string };
}

// SENTINEL_CLIENT_ID — client_admin doesn't need to resolve their own
// clientId on the frontend; the server picks it up from auth. We pass this
// sentinel so the hooks know they have "a" client and should fetch.
const CLIENT_ADMIN_SENTINEL = "__client_admin_self__";

export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const { loading: simsLoading, role, simulations } = useSimulations();
  const [view, setView] = useState<DashboardView>("executive");
  const [period, setPeriod] = useState<DashboardPeriod>("30d");
  const [exportOpen, setExportOpen] = useState(false);

  const allowed = role === "super_admin" || role === "client_admin";

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [cohorts, setCohorts] = useState<CohortRow[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedCohortId, setSelectedCohortId] = useState<string>("");

  // Section pickers drive PDF (summary). Column picker drives CSV (raw rows).
  const [execPick, setExecPick] = useState<Record<ExecSectionKey, boolean>>(
    () => Object.fromEntries(EXEC_SECTIONS.map((s) => [s.key, true])) as Record<ExecSectionKey, boolean>,
  );
  const [mgrPick, setMgrPick] = useState<Record<ManagerSectionKey, boolean>>(
    () => Object.fromEntries(MANAGER_SECTIONS.map((s) => [s.key, true])) as Record<ManagerSectionKey, boolean>,
  );
  const [colPick, setColPick] = useState<ColumnPick>(() => defaultColumnPick());
  const [exportTab, setExportTab] = useState<"pdf" | "csv">("pdf");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (role !== "super_admin") {
      setClientsLoading(false);
      return;
    }
    let alive = true;
    (async (): Promise<void> => {
      try {
        const res = await apiCall<{ clients: ClientRow[] }>("/libraryClients", { method: "GET", timeoutMs: 120_000 });
        if (!alive) return;
        const active = res.clients.filter((c) => c.isActive);
        setClients(active);
        if (active[0] !== undefined) setSelectedClientId((prev) => prev ?? active[0]!.id);
      } finally {
        if (alive) setClientsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [role]);

  const activeClientId = role === "super_admin"
    ? selectedClientId
    : role === "client_admin"
      ? CLIENT_ADMIN_SENTINEL
      : null;

  // Project list — for super_admin: scoped to selected client; for client_admin:
  // returns the projects they have access to (libraryProjects already filters
  // via client_admin_projects).
  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    (async (): Promise<void> => {
      try {
        const body: Record<string, unknown> = {};
        if (role === "super_admin" && selectedClientId !== null) body["clientId"] = selectedClientId;
        const res = await apiCall<{ projects: ProjectRow[] }>("/libraryProjects", { method: "POST", body, timeoutMs: 120_000 });
        if (alive) setProjects(res.projects.filter((p) => p.isActive));
      } catch { if (alive) setProjects([]); }
    })();
    return () => { alive = false; };
  }, [allowed, role, selectedClientId]);

  // Cohort list — depends on selected project (or, if no project, on the client
  // for super_admin / on the user's access for client_admin).
  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    (async (): Promise<void> => {
      try {
        const body: Record<string, unknown> = {};
        if (selectedProjectId !== "") body["projectId"] = selectedProjectId;
        else if (role === "super_admin" && selectedClientId !== null) body["clientId"] = selectedClientId;
        const res = await apiCall<{ cohorts: CohortRow[] }>("/libraryCohortList", { method: "POST", body, timeoutMs: 120_000 });
        if (alive) setCohorts(res.cohorts.filter((c) => c.isActive));
      } catch { if (alive) setCohorts([]); }
    })();
    return () => { alive = false; };
  }, [allowed, role, selectedClientId, selectedProjectId]);

  // When client or project changes, clear deeper picks.
  useEffect(() => { setSelectedProjectId(""); setSelectedCohortId(""); }, [selectedClientId]);
  useEffect(() => { setSelectedCohortId(""); }, [selectedProjectId]);

  const exec = useExecutiveDashboard(
    view === "executive" ? activeClientId : null,
    period,
    selectedProjectId === "" ? null : selectedProjectId,
    selectedCohortId === "" ? null : selectedCohortId,
  );
  const mgr  = useManagerDashboard(
    view === "manager" ? activeClientId : null,
    period,
    selectedProjectId === "" ? null : selectedProjectId,
    selectedCohortId === "" ? null : selectedCohortId,
  );

  if (simsLoading) {
    return (
      <div className="lq-root">
        <div className="lq-container" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <div className="lq-spinner" />
        </div>
      </div>
    );
  }

  if (!allowed) {
    // Learner — push back to the sims list silently.
    void simulations;
    return <Navigate to="/simulations" replace />;
  }

  const clientLabel = (
    role === "super_admin"
      ? (clients.find((c) => c.id === selectedClientId)?.name ?? "Select client")
      : (exec.data?.client.name ?? mgr.data?.client.name ?? "Your client")
  );

  return (
    <div className="lq-root">
      <div className="lq-container">

        <div style={{ marginBottom: 16 }}>
          <AdminNav />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img
              src={neucodeLogoWhite}
              alt="NeuCode Talent"
              style={{ height: 26, width: "auto", display: "block", opacity: 0.95 }}
            />
            <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.1)" }} />
            <div>
              <div style={{ fontSize: 11, color: "#71717A", letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 2 }}>Client</div>
              <div style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.1, letterSpacing: -0.3 }}>{clientLabel}</div>
              <div style={{ fontSize: 11, color: "#71717A", marginTop: 4 }}>
                <span className="lq-pulse" />
                {exec.data !== null ? `${exec.data.totals.learners} leaders · ${exec.data.totals.cohorts} cohorts` :
                 mgr.data !== null ? `${mgr.data.funnel.assigned} assigned` :
                 "Loading…"}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {role === "super_admin" && !clientsLoading && clients.length > 0 && (
              <select
                className="lq-select"
                value={selectedClientId ?? ""}
                onChange={(e) => setSelectedClientId(e.target.value)}
                title="Client"
              >
                {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            )}

            <select
              className="lq-select"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={projects.length === 0}
              title="Project"
            >
              <option value="">All projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{role === "super_admin" ? p.name : p.name}</option>
              ))}
            </select>

            <select
              className="lq-select"
              value={selectedCohortId}
              onChange={(e) => setSelectedCohortId(e.target.value)}
              disabled={cohorts.length === 0}
              title="Cohort"
            >
              <option value="">All cohorts</option>
              {cohorts
                .filter((c) => selectedProjectId === "" || c.project.id === selectedProjectId)
                .map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>

            <div style={{ display: "flex", gap: 2 }}>
              <button className={`lq-chip ${view === "executive" ? "on" : ""}`} onClick={() => setView("executive")}>Executive</button>
              <button className={`lq-chip ${view === "manager" ? "on" : ""}`}   onClick={() => setView("manager")}>Manager</button>
            </div>

            <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.08)", margin: "0 4px" }} />

            <select className="lq-select" value={period} onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>

            <div className="lq-pop">
              <button className="lq-btn" onClick={() => setExportOpen((o) => !o)}>↓ Export</button>
              {exportOpen && (
                <div className="lq-pop-menu open" style={{ minWidth: 280, maxHeight: 460, overflowY: "auto" }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                    <button
                      className={`lq-chip ${exportTab === "pdf" ? "on" : ""}`}
                      style={{ flex: 1 }}
                      onClick={() => setExportTab("pdf")}
                    >PDF summary</button>
                    <button
                      className={`lq-chip ${exportTab === "csv" ? "on" : ""}`}
                      style={{ flex: 1 }}
                      onClick={() => setExportTab("csv")}
                    >CSV raw data</button>
                  </div>

                  {exportTab === "pdf" ? (
                    <>
                      <div style={{ fontSize: 10, color: "#71717A", letterSpacing: 0.6, padding: "4px 7px", marginBottom: 4 }}>
                        PICK SECTIONS TO INCLUDE
                      </div>
                      {view === "executive" ? (
                        EXEC_SECTIONS.map((s) => (
                          <label key={s.key} className="lq-pop-item">
                            <input type="checkbox"
                              checked={execPick[s.key]}
                              onChange={(e) => setExecPick((p) => ({ ...p, [s.key]: e.target.checked }))} />
                            {s.label}
                          </label>
                        ))
                      ) : (
                        MANAGER_SECTIONS.map((s) => (
                          <label key={s.key} className="lq-pop-item">
                            <input type="checkbox"
                              checked={mgrPick[s.key]}
                              onChange={(e) => setMgrPick((p) => ({ ...p, [s.key]: e.target.checked }))} />
                            {s.label}
                          </label>
                        ))
                      )}
                      <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "8px 0" }} />
                      <button
                        className="lq-btn"
                        style={{ width: "100%" }}
                        disabled={generating || (view === "executive" ? exec.data === null : mgr.data === null)}
                        onClick={async () => {
                          setGenerating(true);
                          try {
                            if (view === "executive" && exec.data !== null) {
                              const pdfData = buildExecutivePdfData(exec.data);
                              const blob = await generateExecutiveDashboardPdf(pdfData, execPick);
                              downloadBlob(blob, `${slugForFile(exec.data.client.name)}-executive-dashboard.pdf`);
                            } else if (view === "manager" && mgr.data !== null) {
                              const pdfData = buildManagerPdfData(mgr.data);
                              const blob = await generateManagerDashboardPdf(pdfData, mgrPick);
                              downloadBlob(blob, `${slugForFile(mgr.data.client.name)}-manager-dashboard.pdf`);
                            }
                            setExportOpen(false);
                          } finally { setGenerating(false); }
                        }}
                      >{generating ? "Generating…" : "Download PDF"}</button>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "4px 7px", marginBottom: 4 }}>
                        <div style={{ fontSize: 10, color: "#71717A", letterSpacing: 0.6 }}>PICK COLUMNS · ONE ROW PER ATTEMPT</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => setColPick(Object.fromEntries(RAW_COLUMNS.map((c) => [c.key, true])))}
                            style={{ fontSize: 10, color: "#A1A1AA", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                          >All</button>
                          <span style={{ fontSize: 10, color: "#52525B" }}>·</span>
                          <button
                            type="button"
                            onClick={() => setColPick(defaultColumnPick())}
                            style={{ fontSize: 10, color: "#A1A1AA", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                          >Default</button>
                        </div>
                      </div>
                      {(["Learner", "Cohort", "Sim", "Attempt", "Timing"] as const).map((g) => (
                        <div key={g} style={{ marginBottom: 4 }}>
                          <div style={{ fontSize: 10, color: "#52525B", padding: "4px 7px 2px", letterSpacing: 0.4 }}>{g.toUpperCase()}</div>
                          {RAW_COLUMNS.filter((c) => c.group === g).map((c) => (
                            <label key={c.key} className="lq-pop-item">
                              <input type="checkbox"
                                checked={colPick[c.key] === true}
                                onChange={(e) => setColPick((p) => ({ ...p, [c.key]: e.target.checked }))} />
                              {c.label}
                            </label>
                          ))}
                        </div>
                      ))}
                      <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "8px 0" }} />
                      <button
                        className="lq-btn"
                        style={{ width: "100%" }}
                        disabled={generating || activeClientId === null}
                        onClick={async () => {
                          if (activeClientId === null) return;
                          setGenerating(true);
                          try {
                            const resp = await fetchRawRows(activeClientId, period);
                            const csv = rawRowsToCsv(resp, colPick);
                            const stamp = new Date().toISOString().slice(0, 10);
                            downloadCsv(csv, `${slugForFile(resp.clientName)}-raw-${stamp}.csv`);
                            setExportOpen(false);
                          } finally { setGenerating(false); }
                        }}
                      >{generating ? "Fetching rows…" : "Download CSV"}</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {activeClientId === null ? (
          <div className="lq-glass" style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 14, color: "#A1A1AA" }}>Pick a client to load the dashboard.</div>
          </div>
        ) : view === "executive" ? (
          exec.loading ? <CenteredSpinner /> :
          exec.error !== null ? <ErrorBanner message={exec.error} retry={exec.reload} /> :
          exec.data !== null ? <DashboardExecutiveView data={exec.data} /> :
          <CenteredSpinner />
        ) : (
          mgr.loading ? <CenteredSpinner /> :
          mgr.error !== null ? <ErrorBanner message={mgr.error} retry={mgr.reload} /> :
          mgr.data !== null ? <DashboardManagerView data={mgr.data} /> :
          <CenteredSpinner />
        )}

      </div>
    </div>
  );
}

function CenteredSpinner(): JSX.Element {
  return (
    <div className="lq-glass" style={{ padding: "60px 24px", display: "flex", justifyContent: "center" }}>
      <div className="lq-spinner" />
    </div>
  );
}

function buildExecutivePdfData(d: ExecutiveData): import("@/lib/simPdf").ExecutiveDashboardPdfData {
  // Reuse the theme extractor from the live view? Simpler: build a tiny adapter
  // here so we don't have to import a React-coupled helper into a pure data
  // path. The dashboard PDF takes three concrete themes; if the AI insights
  // payload is missing, the themes array stays empty.
  const themes: Array<{ title: string; body: string; severity: "strong" | "developing" | "needs" }> = [];
  if (d.themes !== null && typeof d.themes.insights === "object" && d.themes.insights !== null) {
    const ins = d.themes.insights as Record<string, unknown>;
    const strengths = typeof ins["strengths"] === "string" ? ins["strengths"] as string : "";
    const development = typeof ins["development"] === "string" ? ins["development"] as string : "";
    const crossTheme = typeof ins["themes"] === "string" ? ins["themes"] as string : "";
    if (development !== "") themes.push({ title: "Development gaps", body: development, severity: "needs" });
    if (crossTheme !== "") themes.push({ title: "Cross-cohort themes", body: crossTheme, severity: "developing" });
    if (strengths !== "")  themes.push({ title: "What's landing", body: strengths, severity: "strong" });
  }

  return {
    clientName: d.client.name,
    period: d.period,
    readiness: d.readiness,
    uplift: d.uplift,
    themes,
    competencies: d.competencies.map((c) => ({ name: c.name, avgScore: c.avgScore, n: c.n })),
    topPerformers: d.topPerformers.map((p, idx) => ({
      rank: idx + 1,
      fullName: p.fullName,
      cohortName: p.cohortName,
      avgScore: p.avgScore,
      n: p.n,
    })),
    standoutCohort: d.standoutCohort,
    concernCohort: d.concernCohort,
  };
}

function buildManagerPdfData(d: ManagerData): import("@/lib/simPdf").ManagerDashboardPdfData {
  return {
    clientName: d.client.name,
    period: d.period,
    funnel: d.funnel,
    actionQueues: {
      notStarted: { total: d.actionQueues.notStarted.total, rows: d.actionQueues.notStarted.rows.map((r) => ({ fullName: r.fullName, cohortName: r.cohortName, assignedAt: r.assignedAt })) },
      stuck:      { total: d.actionQueues.stuck.total,      rows: d.actionQueues.stuck.rows.map((r) => ({ fullName: r.fullName, cohortName: r.cohortName, idleDays: r.idleDays })) },
      atRisk:     { total: d.actionQueues.atRisk.total,     rows: d.actionQueues.atRisk.rows.map((r) => ({ fullName: r.fullName, cohortName: r.cohortName, avgScore: r.avgScore, simCount: r.simCount })) },
    },
    completionBySim: d.completionBySim,
    timeToComplete: d.timeToComplete,
    dropOffMap: d.dropOffMap,
  };
}

function ErrorBanner({ message, retry }: { message: string; retry: () => void }): JSX.Element {
  return (
    <div className="lq-glass lq-glass-r" style={{ padding: "20px 24px" }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: "#FCA5A5" }}>Couldn't load this view</div>
      <div style={{ fontSize: 12, color: "#A1A1AA", marginTop: 6 }}>{message}</div>
      <button className="lq-btn-mini" style={{ marginTop: 12 }} onClick={retry}>Try again</button>
    </div>
  );
}
